'use client';

import { useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
  CarouselDots,
  CarouselControls,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import { getItemTrackId } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { HeroItemContent } from './components/hero-item-content';
import type { HeroItem } from './hero.types';

interface HeroCarouselProps {
  items: HeroItem[];
  autoplay?: boolean;
  showNavigation?: boolean;
  showDots?: boolean;
  /** Tracking namespace from the module (e.g. "hero"); slides are numbered. */
  trackingPrefix?: string;
}

export function HeroCarousel({
  items,
  autoplay,
  showNavigation = true,
  showDots = true,
  trackingPrefix,
}: HeroCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const carouselLabel = useLabel('carousel.heroSlider', 'Hero slider');

  return (
    <Carousel
      setApi={setApi}
      autoplay={autoplay}
      autoplayDelay={5000}
      ariaLabel={carouselLabel}
      className="w-full"
      opts={{
        loop: true,
      }}
    >
      <CarouselContent>
        {items.map((item, index) => (
          <CarouselItem key={item.id}>
            <HeroItemContent
              item={item}
              priority={index === 0}
              trackingPrefix={trackingPrefix ? `${trackingPrefix}-slide-${getItemTrackId(item.id, index)}` : undefined}
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      {(showNavigation || showDots) && (
        <CarouselControls className="mt-10 lg:mt-12">
          {showNavigation && <CarouselNavigation api={api} />}
          {showDots && <CarouselDots api={api} />}
        </CarouselControls>
      )}
    </Carousel>
  );
}
